/**
 * VenueDetailPage ✦ iter230 — /venues/:slug
 *
 * Premium venue portfolio page driven by /api/venues/{slug}/intelligence.
 * Sections:
 *   1. Hero with branded cover + Recommended/Trusted/Partner badge
 *   2. About + quiet attendance line + Venue Intelligence panel
 *   3. EventDrop Confidence Badge (auto-tiered)
 *   4. "We've brought these experiences here" — product experience rail
 *   5. "See How Other Couples Styled This Venue" — inspiration cards
 *   6. Related venues
 *   7. "Love This Setup?" booking journey CTA
 *   8. Recent events grid + Enquiry CTA
 *
 * The intelligence endpoint is a no-op when the venue has no events,
 * so each section auto-hides until its data exists.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import ServicesNav from '../components/services/ServicesNav';
import BrandedPlaceholder from '../components/services/BrandedPlaceholder';
import EnquiryForm from '../components/services/EnquiryForm';
import {
  SERIF, IVORY, IVORY_DIM, IVORY_GHOST, IVORY_FAINT,
  GOLD, GOLD_MUTED, GOLD_FAINT, GOLD_HAIRLINE,
  WALNUT_CARD, PAGE_BG, PILL_BUTTON_PRIMARY, PILL_BUTTON_GHOST,
} from '../components/services/_tokens';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TYPE_LABELS = {
  wedding: 'Weddings',
  prom: 'Proms',
  birthday: 'Birthdays',
  party: 'Parties',
  corporate: 'Corporate',
  anniversary: 'Anniversaries',
  engagement: 'Engagements',
  leavers: 'Leavers',
};
const pluraliseType = (t) => TYPE_LABELS[t] || (`${t.charAt(0).toUpperCase()}${t.slice(1)}s`);

// iter230 — SEO meta helper. Inserts / updates a meta tag without
// duplicating it. Returns nothing — fire-and-forget side effect.
const _setMeta = (name, content, isProperty = false) => {
  if (typeof document === 'undefined' || !content) return;
  const attr = isProperty ? 'property' : 'name';
  let tag = document.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const mediaUrl = (path) => {
  if (!path) return null;
  return path.startsWith('/api/')
    ? `${process.env.REACT_APP_BACKEND_URL}${path}`
    : path;
};

const Stat = ({ value, label, testid }) => (
  <div data-testid={testid || `venue-stat-${String(label).toLowerCase().replace(/[^a-z0-9]/g, '-')}`} style={{
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4,
  }}>
    <div style={{
      fontFamily: SERIF, fontWeight: 500, color: IVORY,
      fontSize: 26, letterSpacing: '-0.01em', lineHeight: 1,
    }}>{value}</div>
    <div style={{
      fontFamily: SERIF, fontStyle: 'italic', color: IVORY_DIM,
      fontSize: 11.5, letterSpacing: '0.14em', textTransform: 'uppercase',
    }}>{label}</div>
  </div>
);

// iter230 — Confidence badge. Auto-rendered from /intelligence.confidence.
// Glowing gold treatment for the Recommended / Partner tiers.
const ConfidenceBadge = ({ confidence, isPartner }) => {
  if (!confidence) return null;
  const isFlagship = isPartner || confidence.tier === 'signature' || confidence.tier === 'preferred';
  return (
    <div data-testid={`venue-confidence-badge-${confidence.tier}`} style={{
      display: 'inline-flex', alignItems: 'center', gap: 14,
      padding: '14px 22px',
      background: isFlagship
        ? 'linear-gradient(135deg, rgba(156,124,46,0.22), rgba(212,170,82,0.10))'
        : WALNUT_CARD,
      border: `1px solid ${isFlagship ? GOLD : GOLD_HAIRLINE}`,
      borderRadius: 999,
      boxShadow: isFlagship ? '0 14px 36px rgba(156,124,46,0.32)' : 'none',
    }}>
      <div style={{
        color: GOLD, fontSize: 18, letterSpacing: 2,
        lineHeight: 1,
      }}>{'★'.repeat(Math.max(2, confidence.stars || 3))}</div>
      <div>
        <div style={{
          fontFamily: SERIF, fontWeight: 500, color: IVORY,
          fontSize: 15, letterSpacing: '-0.005em',
        }}>{confidence.label}</div>
        <div style={{
          fontFamily: SERIF, fontStyle: 'italic', fontSize: 12,
          color: IVORY_DIM, letterSpacing: '0.04em', marginTop: 2,
        }}>{confidence.subtitle}</div>
      </div>
    </div>
  );
};

// iter230 — Partner pill (compact). Shown next to the venue name when
// the venue has hit 10+ EventDrop events.
const PartnerPill = () => (
  <span data-testid="venue-partner-pill" style={{
    display: 'inline-block', marginLeft: 12,
    background: GOLD, color: '#1A120A',
    fontFamily: SERIF, fontStyle: 'italic',
    fontSize: 10.5, letterSpacing: '0.22em', textTransform: 'uppercase',
    padding: '4px 11px', borderRadius: 999,
    verticalAlign: 'middle',
    boxShadow: '0 4px 14px rgba(156,124,46,0.42)',
  }}>✦ Partner Venue</span>
);

const VenueDetailPage = () => {
  const { slug } = useParams();
  const [venue, setVenue] = useState(null);
  const [events, setEvents] = useState([]);
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [intel, setIntel] = useState(null);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [enquiryPkg, setEnquiryPkg] = useState('');
  const [notFound, setNotFound] = useState(false);
  // iter232e — Phase B "Review Everywhere": pull reviews from this
  // venue via the unified read-side feed.
  const [venueReviews, setVenueReviews] = useState({
    reviews: [], count: 0, average_rating: null,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [vRes, pRes, iRes, rRes] = await Promise.all([
          axios.get(`${API}/venues/${slug}`),
          axios.get(`${API}/services/photobooth/packages`),
          axios.get(`${API}/venues/${slug}/intelligence`).catch(() => null),
          axios.get(`${API}/venues/${slug}/reviews?limit=12`).catch(() => null),
        ]);
        if (cancelled) return;
        setVenue(vRes.data.venue);
        setEvents(vRes.data.events || []);
        setPackages(pRes.data.packages || []);
        setAddons(pRes.data.addons || []);
        setIntel(iRes?.data || null);
        setVenueReviews(rRes?.data || { reviews: [], count: 0, average_rating: null });
        if (vRes.data.venue) {
          const n = vRes.data.venue.name;
          const loc = vRes.data.venue.location || '';
          document.title = `${n} ✦ EventDrop — Real events at ${n}${loc ? ', ' + loc : ''}`;
          _setMeta('description',
            `Explore real EventDrop weddings, parties and events at ${n}${loc ? ', ' + loc : ''}. ` +
            `Photobooth galleries, customer reviews and the exact packages used.`);
          _setMeta('og:title', `Real events at ${n} ✦ EventDrop`, true);
          _setMeta('og:description',
            `EventDrop has set up at ${n}${loc ? ' (' + loc + ')' : ''} — see the gallery, reviews and packages.`, true);
          if (vRes.data.venue.image_url) {
            const u = vRes.data.venue.image_url.startsWith('/api/')
              ? `${process.env.REACT_APP_BACKEND_URL}${vRes.data.venue.image_url}`
              : vRes.data.venue.image_url;
            _setMeta('og:image', u, true);
          }
        }
      } catch {
        if (!cancelled) setNotFound(true);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const openEnquiry = (pkgSlug = '') => {
    setEnquiryPkg(pkgSlug);
    setEnquiryOpen(true);
  };

  if (notFound) {
    return (
      <div style={{ minHeight: '100vh', background: PAGE_BG, color: IVORY, fontFamily: SERIF }}>
        <ServicesNav />
        <div style={{ padding: 'clamp(100px, 16vw, 180px) 20px', textAlign: 'center' }}>
          <p style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 18, color: IVORY_DIM }}>
            That venue isn’t in our catalogue yet.
          </p>
          <Link to="/venues" style={{ color: GOLD, marginTop: 18, display: 'inline-block' }}>
            See all venues →
          </Link>
        </div>
      </div>
    );
  }

  if (!venue) {
    return (
      <div style={{ minHeight: '100vh', background: PAGE_BG, color: IVORY, fontFamily: SERIF }}>
        <ServicesNav />
        <div style={{ padding: 'clamp(100px, 16vw, 180px) 20px', textAlign: 'center' }}>
          <p style={{ fontFamily: SERIF, fontStyle: 'italic', color: IVORY_DIM }}>The venue is loading ✦</p>
        </div>
      </div>
    );
  }

  const recommended = packages.find(p => p.slug === venue.recommended_package_slug) || packages[1];
  const stats = intel?.stats || {};
  const confidence = intel?.confidence;
  const isPartner = !!intel?.is_partner;
  const productExperiences = intel?.product_experiences || [];
  const relatedVenues = intel?.related_venues || [];
  const intelEvents = intel?.events || events;

  return (
    <div data-testid="venue-detail-page" style={{
      minHeight: '100vh', background: PAGE_BG, color: IVORY, fontFamily: SERIF,
    }}>
      <ServicesNav active="services" />

      <section style={{ padding: 'clamp(56px, 9vw, 100px) clamp(20px, 5vw, 60px) 0' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <Link to="/venues" data-testid="venue-back" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            color: IVORY_DIM, textDecoration: 'none',
            fontFamily: SERIF, fontStyle: 'italic', fontSize: 13,
            marginBottom: 22,
          }}>← All venues</Link>
          <BrandedPlaceholder
            label={venue.name}
            sublabel={venue.location}
            aspectRatio="21 / 9"
            imageUrl={venue.image_url || venue.cover_image_url}
          />

          {/* iter230 — Confidence + Partner badge row, sits directly
              under the hero so the badge is the first social-proof
              signal a visitor reads. Auto-hides on fresh venues. */}
          {(confidence || isPartner) && (
            <div style={{
              marginTop: 22, display: 'flex', gap: 12, flexWrap: 'wrap',
              alignItems: 'center',
            }} data-testid="venue-trust-row">
              <ConfidenceBadge confidence={confidence} isPartner={isPartner} />
            </div>
          )}

          <div style={{
            marginTop: 26, display: 'grid', gap: 26,
            gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
          }}>
            <div>
              <h1 style={{
                fontFamily: SERIF, fontWeight: 500,
                fontSize: 'clamp(30px, 5vw, 48px)', color: IVORY, margin: 0,
                letterSpacing: '-0.02em',
              }} data-testid="venue-name">
                {venue.name}
                {isPartner && <PartnerPill />}
              </h1>
              <p style={{
                fontFamily: SERIF, fontStyle: 'italic', color: GOLD,
                fontSize: 14, margin: '8px 0 0', letterSpacing: '0.06em',
              }}>{venue.location}</p>
              <p style={{
                fontFamily: SERIF, color: IVORY,
                fontSize: 16, lineHeight: 1.7,
                margin: '24px 0 0', maxWidth: 600,
              }}>{venue.about}</p>

              {venue.event_count > 0 && (
                <p data-testid="venue-attendance-line" style={{
                  fontFamily: SERIF, fontStyle: 'italic',
                  color: GOLD, fontSize: 15, lineHeight: 1.6,
                  margin: '18px 0 0', maxWidth: 600, letterSpacing: '0.01em',
                }}>
                  {venue.event_count === 1
                    ? 'We\u2019ve captured memories here once before.'
                    : `We\u2019ve captured memories here on ${venue.event_count} occasions.`}
                </p>
              )}

              {/* iter230 — Premium Venue Intelligence panel.
                  Renders every stat with data; gaps stay hidden so
                  fresh venues never look hollow. */}
              {stats.events_completed > 0 && (
                <div data-testid="venue-intelligence-panel" style={{
                  marginTop: 30,
                  padding: '22px 24px',
                  background: WALNUT_CARD,
                  border: `1px solid ${GOLD_HAIRLINE}`,
                  borderRadius: 12,
                }}>
                  <div style={{
                    fontFamily: SERIF, fontStyle: 'italic',
                    fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
                    color: GOLD, marginBottom: 16,
                  }}>✦ At this venue</div>
                  <div style={{
                    display: 'grid', gap: 18,
                    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                  }}>
                    <Stat value={stats.events_completed} label="Events" />
                    {stats.average_rating > 0 && (
                      <Stat value={`${stats.average_rating}★`} label="Average Rating" />
                    )}
                    {stats.years_visiting > 0 && (
                      <Stat value={stats.years_visiting} label={stats.years_visiting === 1 ? 'Year' : 'Years Visiting'} />
                    )}
                    {stats.most_popular_package_label && (
                      <Stat
                        value={stats.most_popular_package_label}
                        label="Most Popular"
                        testid="venue-stat-most-popular"
                      />
                    )}
                    {stats.photos_captured > 0 && (
                      <Stat value={stats.photos_captured.toLocaleString()} label="Photos" />
                    )}
                    {stats.audio_messages > 0 && (
                      <Stat value={stats.audio_messages.toLocaleString()} label="Audio Messages" />
                    )}
                    {stats.instant_prints > 0 && (
                      <Stat value={stats.instant_prints.toLocaleString()} label="Instant Prints" />
                    )}
                    {stats.digital_galleries > 0 && (
                      <Stat value={stats.digital_galleries} label="Digital Galleries" />
                    )}
                  </div>
                  {(stats.first_event_date || stats.latest_event_date) && (
                    <div style={{
                      marginTop: 16, paddingTop: 14,
                      borderTop: `1px solid ${GOLD_HAIRLINE}`,
                      display: 'flex', justifyContent: 'space-between',
                      gap: 10, flexWrap: 'wrap',
                      fontFamily: SERIF, fontStyle: 'italic', fontSize: 12.5,
                      color: IVORY_DIM,
                    }}>
                      {stats.first_event_date && (
                        <span data-testid="venue-first-visit">
                          First EventDrop visit ✦ {stats.first_event_date.slice(0, 10)}
                        </span>
                      )}
                      {stats.latest_event_date && (
                        <span data-testid="venue-latest-visit">
                          Latest visit ✦ {stats.latest_event_date.slice(0, 10)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{
              background: WALNUT_CARD,
              border: `1px solid ${GOLD_FAINT}`,
              borderRadius: 12,
              padding: '22px 22px 24px',
              alignSelf: 'start',
            }}>
              <div style={{
                fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
                color: GOLD, marginBottom: 14,
              }}>✦ Recommended setup</div>
              {recommended && (
                <>
                  <h3 style={{
                    fontFamily: SERIF, fontWeight: 500, fontSize: 19,
                    margin: '0 0 6px', color: IVORY,
                  }}>{recommended.name}</h3>
                  <div style={{
                    fontFamily: SERIF, fontWeight: 600,
                    fontSize: 26, color: IVORY, marginBottom: 12,
                  }}>£{recommended.price}</div>
                </>
              )}
              <p style={{
                fontFamily: SERIF, fontStyle: 'italic', fontSize: 13.5,
                color: IVORY_DIM, lineHeight: 1.55, margin: '0 0 18px',
              }}>{venue.booth_recommendation}</p>
              <button
                type="button"
                onClick={() => openEnquiry(recommended?.slug || '')}
                data-testid="venue-cta-enquire"
                style={{ ...PILL_BUTTON_PRIMARY, width: '100%' }}
              >Enquire for this venue ✦</button>
            </div>
          </div>
        </div>
      </section>

      {/* iter230 — Product experience rail. Auto-hides when no
          packages have been used at this venue yet. */}
      {productExperiences.length > 0 && (
        <section data-testid="venue-product-experiences" style={{
          padding: 'clamp(56px, 8vw, 90px) clamp(20px, 5vw, 60px)',
          borderTop: `1px solid ${GOLD_HAIRLINE}`, marginTop: 80,
        }}>
          <div style={{ maxWidth: 1080, margin: '0 auto' }}>
            <SectionEyebrow>✦ Brought here</SectionEyebrow>
            <SectionHeading>We&rsquo;ve brought these experiences here</SectionHeading>
            <p style={{
              fontFamily: SERIF, fontStyle: 'italic', textAlign: 'center',
              fontSize: 14, color: IVORY_DIM, maxWidth: 580,
              margin: '12px auto 36px', lineHeight: 1.6,
            }}>
              Every product below has already been set up at {venue.name}.
              Tap any of them to explore the same setup for your night.
            </p>
            <div style={{
              display: 'grid', gap: 18,
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            }}>
              {productExperiences.map(pe => (
                <Link
                  key={pe.slug}
                  to={`/venues?package=${encodeURIComponent(pe.slug)}`}
                  data-testid={`venue-product-${pe.slug}`}
                  style={{
                    background: WALNUT_CARD, border: `1px solid ${GOLD_FAINT}`,
                    borderRadius: 12, padding: '18px 20px',
                    textDecoration: 'none', color: IVORY,
                    display: 'flex', flexDirection: 'column', gap: 8,
                    transition: 'transform 200ms ease, border-color 200ms ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.borderColor = GOLD;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = GOLD_FAINT;
                  }}
                >
                  <div style={{
                    fontFamily: SERIF, fontStyle: 'italic',
                    fontSize: 10.5, letterSpacing: '0.22em', textTransform: 'uppercase',
                    color: GOLD,
                  }}>✦ Used here {pe.count}×</div>
                  <div style={{
                    fontFamily: SERIF, fontWeight: 500, fontSize: 18,
                    color: IVORY, letterSpacing: '-0.005em',
                  }}>{pe.label}</div>
                  <div style={{
                    fontFamily: SERIF, fontStyle: 'italic',
                    fontSize: 12.5, color: IVORY_DIM, marginTop: 'auto',
                  }}>View on map →</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* iter230 — Inspiration: "See How Other Couples Styled This Venue" */}
      {intelEvents.length > 0 && (
        <section data-testid="venue-inspiration" style={{
          padding: 'clamp(56px, 8vw, 90px) clamp(20px, 5vw, 60px)',
          borderTop: `1px solid ${GOLD_HAIRLINE}`,
        }}>
          <div style={{ maxWidth: 1180, margin: '0 auto' }}>
            <SectionEyebrow>✦ Inspiration</SectionEyebrow>
            <SectionHeading>
              {intelEvents.length === 1
                ? 'See how this venue was styled'
                : 'See how other couples styled this venue'}
            </SectionHeading>
            <div style={{
              marginTop: 40,
              display: 'grid', gap: 22,
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            }}>
              {intelEvents.slice(0, 6).map(ev => (
                <Link
                  key={ev.event_id || ev.slug}
                  to={`/events/${ev.slug}`}
                  data-testid={`venue-inspiration-${ev.slug}`}
                  style={{
                    background: WALNUT_CARD, border: `1px solid ${GOLD_HAIRLINE}`,
                    borderRadius: 12, overflow: 'hidden', textDecoration: 'none',
                    color: IVORY, display: 'flex', flexDirection: 'column',
                    transition: 'transform 220ms ease, border-color 220ms ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.borderColor = GOLD;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = GOLD_HAIRLINE;
                  }}
                >
                  {ev.cover_image ? (
                    <div style={{
                      backgroundImage: `url(${mediaUrl(ev.cover_image)})`,
                      backgroundSize: 'cover', backgroundPosition: 'center',
                      aspectRatio: '4 / 3',
                    }} />
                  ) : (
                    <BrandedPlaceholder label={ev.title || ev.event_type} aspectRatio="4 / 3" />
                  )}
                  <div style={{ padding: '18px 20px 22px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{
                        fontFamily: SERIF, fontStyle: 'italic',
                        fontSize: 10.5, letterSpacing: '0.22em', textTransform: 'uppercase',
                        color: GOLD,
                      }}>{ev.event_type || 'Event'}</span>
                      {ev.event_date && (
                        <span style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 12, color: IVORY_DIM }}>
                          {ev.event_date.slice(0, 10)}
                        </span>
                      )}
                    </div>
                    <h4 style={{
                      fontFamily: SERIF, fontWeight: 500, fontSize: 17,
                      margin: '8px 0 4px', color: IVORY, letterSpacing: '-0.005em',
                    }}>{ev.title || ev.customer_name || 'EventDrop event'}</h4>
                    {ev.package_label && (
                      <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 12.5, color: IVORY_DIM }}>
                        {ev.package_label}
                      </div>
                    )}
                    {ev.review_rating > 0 && (
                      <div style={{ color: GOLD, fontSize: 13, letterSpacing: 1.5, marginTop: 8 }}>
                        {'★'.repeat(Math.round(ev.review_rating))}
                      </div>
                    )}
                    {ev.review_snippet && (
                      <p style={{
                        fontFamily: SERIF, fontStyle: 'italic',
                        fontSize: 13, color: IVORY, lineHeight: 1.55,
                        margin: '8px 0 0',
                      }}>&ldquo;{ev.review_snippet.slice(0, 130)}{ev.review_snippet.length > 130 ? '…' : ''}&rdquo;</p>
                    )}
                    <div style={{
                      marginTop: 'auto', paddingTop: 14,
                      fontFamily: SERIF, fontStyle: 'italic',
                      fontSize: 12.5, color: GOLD,
                    }}>View gallery →</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* iter230 — Related venues */}
      {relatedVenues.length > 0 && (
        <section data-testid="venue-related" style={{
          padding: 'clamp(56px, 8vw, 90px) clamp(20px, 5vw, 60px)',
          borderTop: `1px solid ${GOLD_HAIRLINE}`,
        }}>
          <div style={{ maxWidth: 1180, margin: '0 auto' }}>
            <SectionEyebrow>✦ Nearby</SectionEyebrow>
            <SectionHeading>Related venues you might love</SectionHeading>
            <div style={{
              marginTop: 36,
              display: 'grid', gap: 18,
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            }}>
              {relatedVenues.map(rv => (
                <Link
                  key={rv.slug}
                  to={`/venues/${rv.slug}`}
                  data-testid={`venue-related-${rv.slug}`}
                  style={{
                    background: WALNUT_CARD, border: `1px solid ${GOLD_HAIRLINE}`,
                    borderRadius: 12, overflow: 'hidden', textDecoration: 'none',
                    color: IVORY,
                  }}
                >
                  {rv.image_url ? (
                    <div style={{
                      backgroundImage: `url(${mediaUrl(rv.image_url)})`,
                      backgroundSize: 'cover', backgroundPosition: 'center',
                      aspectRatio: '4 / 3',
                    }} />
                  ) : (
                    <BrandedPlaceholder label={rv.name} aspectRatio="4 / 3" />
                  )}
                  <div style={{ padding: '16px 18px 20px' }}>
                    <h4 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 16, margin: 0, color: IVORY }}>
                      {rv.name}
                    </h4>
                    {rv.location && (
                      <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 12, color: IVORY_DIM, marginTop: 4 }}>
                        {rv.location}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* iter230 — "Love This Setup?" booking journey CTA */}
      {productExperiences.length > 0 && (
        <section data-testid="venue-love-this-setup" style={{
          padding: 'clamp(60px, 9vw, 100px) clamp(20px, 5vw, 60px)',
          borderTop: `1px solid ${GOLD_HAIRLINE}`,
          background: 'linear-gradient(180deg, rgba(156,124,46,0.06), transparent)',
        }}>
          <div style={{ maxWidth: 880, margin: '0 auto', textAlign: 'center' }}>
            <SectionEyebrow>✦ Book the same setup</SectionEyebrow>
            <SectionHeading>Love this setup?</SectionHeading>
            <p style={{
              fontFamily: SERIF, fontStyle: 'italic',
              color: IVORY_DIM, fontSize: 'clamp(14px, 1.4vw, 16px)',
              margin: '14px auto 30px', maxWidth: 540, lineHeight: 1.6,
            }}>
              Bring the exact products we&rsquo;ve already set up at {venue.name} to your night.
            </p>
            <div style={{
              display: 'grid', gap: 14,
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              textAlign: 'left',
            }}>
              {productExperiences.slice(0, 4).map(pe => {
                const matchedPkg = packages.find(p => p.slug === pe.slug);
                return (
                  <div
                    key={pe.slug}
                    data-testid={`love-this-setup-${pe.slug}`}
                    style={{
                      background: WALNUT_CARD, border: `1px solid ${GOLD_FAINT}`,
                      borderRadius: 12, padding: '20px 22px',
                      display: 'flex', flexDirection: 'column', gap: 12,
                    }}
                  >
                    <div>
                      <div style={{
                        fontFamily: SERIF, fontWeight: 500, fontSize: 17,
                        color: IVORY, letterSpacing: '-0.005em',
                      }}>{pe.label}</div>
                      {matchedPkg?.price > 0 && (
                        <div style={{
                          fontFamily: SERIF, color: GOLD, fontSize: 13.5,
                          marginTop: 4, letterSpacing: '0.02em',
                        }}>From £{matchedPkg.price}</div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <Link
                        to={`/venues?package=${encodeURIComponent(pe.slug)}`}
                        data-testid={`love-view-package-${pe.slug}`}
                        style={{
                          ...PILL_BUTTON_GHOST,
                          fontSize: 11.5, padding: '8px 14px',
                          textDecoration: 'none', display: 'inline-block',
                        }}
                      >✓ View Package</Link>
                      <button
                        type="button"
                        onClick={() => openEnquiry(pe.slug)}
                        data-testid={`love-check-${pe.slug}`}
                        style={{
                          ...PILL_BUTTON_GHOST,
                          fontSize: 11.5, padding: '8px 14px',
                          cursor: 'pointer',
                        }}
                      >✓ Check Availability</button>
                      <button
                        type="button"
                        onClick={() => openEnquiry(pe.slug)}
                        data-testid={`love-enquire-${pe.slug}`}
                        style={{
                          ...PILL_BUTTON_PRIMARY,
                          fontSize: 11.5, padding: '8px 14px',
                          cursor: 'pointer',
                        }}
                      >✓ Enquire</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* iter232e — Phase B · Reviews from this venue.
          Read from the unified /venues/{slug}/reviews feed so every
          approved review (whether from the new Event Publisher
          submission flow or backfilled from historic events) shows
          here automatically. */}
      {venueReviews.count > 0 && (
        <section data-testid="venue-reviews" style={{
          padding: 'clamp(60px, 9vw, 100px) clamp(20px, 5vw, 60px)',
          borderTop: `1px solid ${GOLD_HAIRLINE}`, marginTop: 60,
        }}>
          <div style={{ maxWidth: 1080, margin: '0 auto' }}>
            <SectionEyebrow>✦ Reviews from this venue</SectionEyebrow>
            <SectionHeading>
              What couples say about EventDrop at {venue.name}
            </SectionHeading>
            {venueReviews.average_rating && (
              <div style={{
                fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 14, color: IVORY_DIM,
                marginTop: 12, letterSpacing: '0.02em',
              }}>
                <span style={{ color: GOLD, fontSize: 16 }}>
                  {'★'.repeat(Math.round(venueReviews.average_rating))}
                </span>{' '}
                {venueReviews.average_rating.toFixed(1)} average · {venueReviews.count} review{venueReviews.count === 1 ? '' : 's'}
              </div>
            )}
            <div style={{
              marginTop: 36, display: 'grid', gap: 18,
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            }}>
              {venueReviews.reviews.map(r => (
                <article key={r.review_id}
                  data-testid={`venue-review-${r.review_id}`}
                  style={{
                    background: WALNUT_CARD,
                    border: `1px solid ${GOLD_HAIRLINE}`,
                    borderRadius: 12, padding: '20px 22px',
                  }}>
                  <div style={{ color: GOLD, fontSize: 17, letterSpacing: '0.06em' }}>
                    {'★'.repeat(r.rating || 5)}
                  </div>
                  <p style={{
                    fontFamily: SERIF, fontStyle: 'italic',
                    fontSize: 15, lineHeight: 1.55, color: IVORY,
                    margin: '12px 0 14px',
                  }}>&ldquo;{r.review_text}&rdquo;</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ fontFamily: SERIF, fontSize: 13, color: IVORY_DIM, letterSpacing: '0.04em' }}>
                      {r.customer_name}
                      {r.event_date ? ` · ${r.event_date.slice(0, 10)}` : ''}
                    </div>
                    {r.event_slug && (
                      <Link to={`/events/${r.event_slug}`}
                        data-testid={`venue-review-link-${r.review_id}`}
                        style={{
                          color: GOLD, fontFamily: SERIF, fontStyle: 'italic',
                          fontSize: 12.5, textDecoration: 'underline',
                        }}>View event →</Link>
                    )}
                  </div>
                  {(r.booths_used || []).length > 0 && (
                    <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {(r.booths_used || []).slice(0, 4).map(b => (
                        <span key={b} style={{
                          fontFamily: SERIF, fontSize: 11, color: GOLD_MUTED,
                          border: `1px solid ${GOLD_HAIRLINE}`,
                          padding: '3px 9px', borderRadius: 999,
                          letterSpacing: '0.04em',
                        }}>{b.replace(/-/g, ' ')}</span>
                      ))}
                    </div>
                  )}
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Legacy events grid stays — used by venues with public events
          that haven't been promoted into the intelligence panel yet. */}
      {events.length > 0 && !productExperiences.length && (
        <section style={{
          padding: 'clamp(70px, 10vw, 120px) clamp(20px, 5vw, 60px)',
          borderTop: `1px solid ${GOLD_HAIRLINE}`, marginTop: 80,
        }}>
          <div style={{ maxWidth: 1080, margin: '0 auto' }}>
            <SectionEyebrow>✦ Events at this venue</SectionEyebrow>
            <SectionHeading>Nights captured at {venue.name}</SectionHeading>
            <div style={{
              marginTop: 40,
              display: 'grid', gap: 18,
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            }}>
              {events.map(ev => (
                <Link
                  key={ev.slug}
                  to={`/events/${ev.slug}`}
                  data-testid={`venue-event-${ev.slug}`}
                  style={{
                    background: WALNUT_CARD, border: `1px solid ${GOLD_HAIRLINE}`,
                    borderRadius: 12, overflow: 'hidden', textDecoration: 'none',
                    color: IVORY,
                  }}
                >
                  <BrandedPlaceholder label={ev.name} aspectRatio="4 / 3" imageUrl={ev.image_url} />
                  <div style={{ padding: '18px 20px' }}>
                    <h4 style={{
                      fontFamily: SERIF, fontWeight: 500, fontSize: 17,
                      margin: '0 0 4px', letterSpacing: '-0.005em',
                    }}>{ev.name}</h4>
                    <div style={{ fontFamily: SERIF, fontStyle: 'italic', color: IVORY_DIM, fontSize: 12.5 }}>
                      {ev.date}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {enquiryOpen && (
        <EnquiryForm
          packages={packages}
          addons={addons}
          initialPackageSlug={enquiryPkg || recommended?.slug || ''}
          onClose={() => setEnquiryOpen(false)}
          sourcePage={`venue:${slug}`}
        />
      )}
    </div>
  );
};

const SectionEyebrow = ({ children }) => (
  <div style={{
    fontFamily: SERIF, fontStyle: 'italic',
    fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
    color: GOLD_MUTED, textAlign: 'center', marginBottom: 12,
  }}>{children}</div>
);

const SectionHeading = ({ children }) => (
  <h2 style={{
    fontFamily: SERIF, fontWeight: 500, textAlign: 'center',
    fontSize: 'clamp(26px, 4vw, 38px)', letterSpacing: '-0.02em',
    color: IVORY, margin: 0, lineHeight: 1.1,
  }}>{children}</h2>
);

export default VenueDetailPage;
